import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Table from 'react-bootstrap/Table';
import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup'
import { nanoid } from 'nanoid';
import { useEffect, useState } from 'react';
import { SpecialServices } from '../SpecialServices';
import { currencies, departmentNotes, signatureOptions, subPackagingTypes } from '../../utils/dropdownOptions';
import { ShipmentContentsModal } from './ShipmentContents';
import { LoadingSpinner } from '../LoadingSpinner';

const initialFormData = {
    weightUnit: 'LB',
    dimensions: {
        dimensionsUnit: 'IN'
    },
    declaredValueCurrency: 'USD',
    identicalPackages: false
}

export const MultiplePieceShipmentModal = (props) => {
    const {
        formik,
        show,
        handleClose,
        revertMpsPackages,
        submitLoading,
        getRateQuoteAndTransitTimes,
        rateQuoteLoading,
        shipmentRateQuoteValue,
        onReturnTab
    } = props;

    const values = onReturnTab ? formik.values.returnData : formik.values;
    const { mpsPackages, numPackages } = values;
    const [mpsFormData, setMpsFormData] = useState(initialFormData);
    const [selectedPackageIds, setSelectedPackageIds] = useState([]);
    const [showSpecialServices, setShowSpecialServices] = useState(false);
    const [showContentsModal, setShowContentsModal] = useState(false);
    const [shipmentContents, setShipmentContents] = useState([]);
    const [contentsOnOpen, setContentsOnOpen] = useState([]);
    const mpsPackagesField = onReturnTab ? 'returnData.mpsPackages' : 'mpsPackages';
    const replaceNumPackagesValueField = onReturnTab ? 'returnData.replaceNumPackagesValue' : 'replaceNumPackagesValue';
    const specialServicesField = onReturnTab ? 'returnData.specialServices' : 'specialServices';

    useEffect(() => {
        // TODO - eliminated excessive rate quote API calls by adding "show" prop here as part of condition, can improve to eliminate unecessary re-renders/useEffect calls as well?
        if (!onReturnTab && mpsPackages.length > 0 && show) {
            getRateQuoteAndTransitTimes(false);
        }
    }, [mpsPackages])

    const handleShowContentsModal = () => {
        setContentsOnOpen(shipmentContents);
        setShowContentsModal(true)
    };
    const handleCloseContentsModal = () => setShowContentsModal(false);

    const updateShipmentContents = (data) => {
        setShipmentContents(data);
    }

    const revertShipmentContents = () => {
        setShipmentContents(contentsOnOpen);
    }

    const handleChange = (e) => {
        const { name, value } = e.target;
        setMpsFormData({ ...mpsFormData, [name]: value });
    }

    const handleCheckChange = (e) => {
        const { name, checked } = e.target;
        setMpsFormData({ ...mpsFormData, [name]: checked });
    }

    const handleDimensionsChange = (e) => {
        const { name, value } = e.target;
        const dimensions = { ...mpsFormData.dimensions, [name]: value };
        setMpsFormData({ ...mpsFormData, dimensions })
    }

    const addPackage = () => {
        if (values.country && values.country !== 'US') {
            if (values.packageContains === 'commodity') {
                if (values.commodities.length === 0) {
                    return alert('At least one commodity must be added to the shipment. Click the Save and Exit button and then select the Commodities tab to complete the shipment details.');
                }
            }
        }
        // TODO - form field validation?
        const newPackage = { ...mpsFormData, id: nanoid() };
        delete newPackage.identicalPackages;
        delete newPackage.numPackages;
        const packageSpecialServices = values.specialServices.filter((i) => (i.selected === true && i.level === 'package'));
        if (packageSpecialServices.length > 0) {
            newPackage.specialServices = packageSpecialServices;
        }
        if (shipmentContents.length > 0) {
            newPackage.contents = shipmentContents;
        }
        if (mpsFormData.identicalPackages && (mpsFormData.numPackages && mpsFormData.numPackages > 1)) {
            const packageArray = [];
            for (let i = 0; i < mpsFormData.numPackages; i++) {
                packageArray.push({ ...newPackage, id: nanoid() });
            }
            formik.setFieldValue(mpsPackagesField, [...mpsPackages, ...packageArray]);
        } else {
            formik.setFieldValue(mpsPackagesField, [...mpsPackages, newPackage]);
        }
        if (mpsPackages.length + 1 > numPackages) {
            formik.setFieldValue(replaceNumPackagesValueField, true);
        }
        setMpsFormData(initialFormData);
        formik.setFieldValue(specialServicesField, values.specialServices.map((i) => {
            if (i.level === 'package') {
                return { ...i, selected: false };
            } else {
                return i;
            }
        }));
        setShipmentContents([]);
        if (showSpecialServices) setShowSpecialServices(false);
    }

    const updatePackage = () => {
        const id = selectedPackageIds[0];
        const updatedPackage = { ...mpsFormData };
        const originalPackage = mpsPackages.find((i) => i.id === id);
        const packageSpecialServices = values.specialServices.filter((i) => (i.selected === true && i.level === 'package'));
        if (originalPackage.specialServices !== packageSpecialServices) {
            updatedPackage.specialServices = packageSpecialServices;
        }
        if (originalPackage.contents !== shipmentContents) {
            updatedPackage.contents = shipmentContents;
        }
        formik.setFieldValue(mpsPackagesField, mpsPackages.map((item) => (
            item.id === id ? { ...updatedPackage, id } : item
        )));
        setMpsFormData(initialFormData);
        setSelectedPackageIds([]);
        resetSpecialServices();
        setShipmentContents([]);
        if (showSpecialServices) setShowSpecialServices(false);
    }

    const handlePackageCheckChange = (e) => {
        const { value, checked } = e.target;
        if (selectedPackageIds.length === 0) {
            const selectedPackage = mpsPackages.find((i) => i.id === value);
            setMpsFormData(selectedPackage);
            setSelectedSpecialServicesAndContents(selectedPackage);
        } else {
            setMpsFormData(initialFormData);
            setShipmentContents([]);
            resetSpecialServices();
            if (showSpecialServices) setShowSpecialServices(false);
        }
        if (checked) {
            setSelectedPackageIds([...selectedPackageIds, value]);
        } else {
            setSelectedPackageIds(selectedPackageIds.filter((i) => i !== value));
        }
    }

    const setSelectedSpecialServicesAndContents = (selectedPackage) => {
        if (selectedPackage.contents && selectedPackage.contents.length > 0) {
            setShipmentContents(selectedPackage.contents);
        }
        if (selectedPackage.specialServices && selectedPackage.specialServices.length > 0) {
            const packageServiceTypes = selectedPackage.specialServices.map((i) => i.specialServiceType);
            const selectedPackageSpecialServiceState = values.specialServices.map((item) => {
                if (packageServiceTypes.includes(item.specialServiceType)) {
                    // if the service is selected on the package, just replace the formik state obj with the package's obj
                    const obj = selectedPackage.specialServices.find((i) => i.specialServiceType === item.specialServiceType);
                    return obj;
                } else {
                    return item;
                }
            });
            formik.setFieldValue(specialServicesField, selectedPackageSpecialServiceState);
            if (!showSpecialServices) setShowSpecialServices(true);
        }
    }

    const resetSpecialServices = () => {
        const updatedSpecialServices = values.specialServices.map((item) => {
            if (item.level === 'package') {
                if (item.details) {
                    const copy = { ...item };
                    delete copy.details;
                    return { ...copy, selected: false };
                }
                return { ...item, selected: false };
            } else {
                return item; // want to keep shipment level selections selected and visible
            }
        })
        formik.setFieldValue(specialServicesField, updatedSpecialServices);
    }

    const removePackages = () => {
        formik.setFieldValue(mpsPackagesField, mpsPackages.filter((i) => !selectedPackageIds.includes(i.id)));
        if ((mpsPackages.length - selectedPackageIds.length === 0) || (mpsPackages.length - selectedPackageIds.length < numPackages)) {
            formik.setFieldValue(replaceNumPackagesValueField, false);
        }
        setSelectedPackageIds([]);
        setMpsFormData(initialFormData);
        if (showSpecialServices) setShowSpecialServices(false);
    }

    const closeWithoutSaving = () => {
        revertMpsPackages();
        handleClose();
        setSelectedPackageIds([]);
    }

    const repeatPreviousPackage = () => {
        const previousPackage = mpsPackages[mpsPackages.length - 1];
        setMpsFormData(previousPackage);
        setSelectedSpecialServicesAndContents(previousPackage);
    }

    return (
        <Modal show={show} onHide={closeWithoutSaving} backdrop='static'>
            <Modal.Header closeButton>Multiple-Piece Shipment Information</Modal.Header>
            <Modal.Body>
                <div className='mps-info'>
                    <fieldset>
                        <legend>Shipment information</legend>
                        <Form.Check
                            className='form-group'
                            checked
                            disabled
                            label='Print labels after I have completed this shipment'
                        />
                        <Form.Group className='form-group'>
                            <Form.Label>Delivery instructions</Form.Label>
                            <Form.Control
                                as='textarea'
                                name={onReturnTab ? 'returnData.deliveryInstructions' : 'deliveryInstructions'}
                                {...formik.getFieldProps(onReturnTab ? 'returnData.deliveryInstructions' : 'deliveryInstructions')}
                            />
                        </Form.Group>
                    </fieldset>
                    <fieldset>
                        <legend>Shipment summary</legend>
                        <div className='mps-details'>
                            <p>Total shipment weight: {mpsPackages.length ? mpsPackages.map((i) => i.weight).reduce((prev, curr) => parseFloat(prev) + parseFloat(curr)) : 0} lbs</p>
                            <p>Total declared value: ${mpsPackages.length ? mpsPackages.map((i) => i.declaredValue).reduce((prev, curr) => parseFloat(prev) + parseFloat(curr)) : 0}</p>
                            {!onReturnTab && <p>Total shipment rate quote: ${rateQuoteLoading ? <LoadingSpinner /> : shipmentRateQuoteValue}</p>}
                        </div>
                    </fieldset>
                </div>
                <fieldset>
                    <legend>Package information - New Package</legend>
                    <div className='mps-info'>
                        <div>
                            <Button
                                onClick={repeatPreviousPackage}
                                variant='secondary'
                                size='sm'
                                className='form-group'
                                disabled={mpsPackages.length === 0 || selectedPackageIds.length > 0}
                            >
                                Repeat previous package
                            </Button>
                            <Form.Group className='form-group'>
                                <Form.Label>Package weight</Form.Label>
                                <InputGroup>
                                    <Form.Control
                                        type='number'
                                        onWheel={(e) => e.target.blur()}
                                        name='weight'
                                        value={mpsFormData.weight || ''}
                                        onChange={handleChange}
                                    />
                                    <Form.Select
                                        name='weightUnit'
                                        value={mpsFormData.weightUnit || ''}
                                        onChange={handleChange}
                                    >
                                        <option value=''>Select an option</option>
                                        <option value='LB'>lbs</option>
                                        <option value='KG'>kgs</option>
                                    </Form.Select>
                                </InputGroup>
                            </Form.Group>
                            {values.country === 'CA' &&
                                <Form.Group className='form-group'>
                                    <Form.Label>Sub-packaging type</Form.Label>
                                    <Form.Select
                                        name='subPackagingType'
                                        value={mpsFormData.subPackagingType || ''}
                                        onChange={handleChange}
                                    >
                                        <option value=''>Select an option</option>
                                        {
                                            subPackagingTypes.map((i) => (
                                                <option key={i} value={i}>{i}</option>
                                            ))
                                        }
                                    </Form.Select>
                                </Form.Group>
                            }
                            <Form.Group className='form-group'>
                                <Form.Label>Package dimensions</Form.Label>
                                <InputGroup>
                                    <Form.Control
                                        type='number'
                                        onWheel={(e) => e.target.blur()}
                                        placeholder='length'
                                        name='length'
                                        value={(mpsFormData.dimensions && mpsFormData.dimensions.length) || ''}
                                        onChange={handleDimensionsChange}
                                    />
                                    <InputGroup.Text>x</InputGroup.Text>
                                    <Form.Control
                                        type='number'
                                        onWheel={(e) => e.target.blur()}
                                        placeholder='width'
                                        name='width'
                                        value={(mpsFormData.dimensions && mpsFormData.dimensions.width) || ''}
                                        onChange={handleDimensionsChange}
                                    />
                                    <InputGroup.Text>x</InputGroup.Text>
                                    <Form.Control
                                        type='number'
                                        onWheel={(e) => e.target.blur()}
                                        placeholder='height'
                                        name='height'
                                        value={(mpsFormData.dimensions && mpsFormData.dimensions.height) || ''}
                                        onChange={handleDimensionsChange}
                                    />
                                    <Form.Select
                                        name='dimensionsUnit'
                                        value={(mpsFormData.dimensions && mpsFormData.dimensions.dimensionsUnit) || ''}
                                        onChange={handleDimensionsChange}
                                    >
                                        <option value=''>Select an option</option>
                                        <option value='IN'>in</option>
                                        <option value='CM'>cm</option>
                                    </Form.Select>
                                </InputGroup>
                            </Form.Group>
                            <Form.Group className='form-group'>
                                <Form.Label>Package description</Form.Label>
                                <Form.Control
                                    type='text'
                                    name='packageDescription'
                                    value={mpsFormData.packageDescription || ''}
                                    onChange={handleChange}
                                />
                            </Form.Group>
                            <Form.Group className='form-group'>
                                <Form.Label>Declared value</Form.Label>
                                <InputGroup>
                                    <Form.Control
                                        type='number'
                                        onWheel={(e) => e.target.blur()}
                                        name='declaredValue'
                                        value={mpsFormData.declaredValue || ''}
                                        onChange={handleChange}
                                    />
                                    <Form.Select
                                        name='declaredValueCurrency'
                                        value={mpsFormData.declaredValueCurrency || ''}
                                        onChange={handleChange}
                                    >
                                        <option value=''>Select an option</option>
                                        {
                                            currencies.map((i) => (
                                                <option key={i.code} value={i.code}>{i.code} - {i.currency}</option>
                                            ))
                                        }
                                    </Form.Select>
                                </InputGroup>
                            </Form.Group>
                            <Form.Group className='form-group'>
                                <Form.Label>Customer reference</Form.Label>
                                <Form.Control
                                    disabled
                                    name='customerReference'
                                    value={values.customerReference}
                                />
                            </Form.Group>
                            <Form.Group className='form-group'>
                                <Form.Label>P.O. number</Form.Label>
                                <Form.Control
                                    disabled
                                    name='poNum'
                                    value={values.poNum}
                                />
                            </Form.Group>
                            <Form.Group className='form-group'>
                                <Form.Label>Invoice number</Form.Label>
                                <Form.Control
                                    disabled
                                    name='invoiceNum'
                                    value={values.invoiceNum}
                                />
                            </Form.Group>
                            <Form.Group className='form-group'>
                                <Form.Label>Department notes</Form.Label>
                                <InputGroup>
                                    <Form.Select
                                        disabled
                                        name='departmentNotesCode'
                                        value={values.departmentNotesCode}
                                    >
                                        <option value=''>Select an option</option>
                                        {
                                            departmentNotes.map((i) => (
                                                <option key={i.code} value={i.code}>{i.code}</option>
                                            ))
                                        }
                                    </Form.Select>
                                    <Form.Control
                                        disabled
                                        name='departmentNotesName'
                                        value={values.departmentNotesName}
                                    />
                                </InputGroup>
                            </Form.Group>
                            {
                                showSpecialServices &&
                                <>
                                    <fieldset>
                                        <legend>Special services</legend>
                                        <SpecialServices formik={formik} shortcutVersion={true} inMpsModal={true} isReturnShipmentTab={onReturnTab} />
                                    </fieldset>
                                    <fieldset>
                                        <legend>Signature options</legend>
                                        <Form.Group className='form-group'>
                                            <Form.Label>Signature options</Form.Label>
                                            <Form.Select
                                                name='signatureOption'
                                                value={mpsFormData.signatureOption || ''}
                                                onChange={handleChange}
                                            >
                                                <option value=''>Select an option</option>
                                                {
                                                    signatureOptions.map((i) => (
                                                        <option key={i.code} value={i.code}>{i.displayText}</option>
                                                    ))
                                                }
                                            </Form.Select>
                                        </Form.Group>
                                    </fieldset>
                                </>
                            }
                            <br />
                            <InputGroup>
                                <InputGroup.Checkbox
                                    name='identicalPackages'
                                    checked={mpsFormData.identicalPackages}
                                    onChange={handleCheckChange}
                                    disabled={selectedPackageIds.length > 0}
                                />
                                <InputGroup.Text className='group-check-label'>Identical packages</InputGroup.Text>
                                <Form.Control
                                    name='numPackages'
                                    value={mpsFormData.numPackages || ''}
                                    onChange={handleChange}
                                    type='number'
                                    onWheel={(e) => e.target.blur()}
                                    disabled={!mpsFormData.identicalPackages}
                                    placeholder='# packages'
                                />
                            </InputGroup>
                            <br />
                            <div className='mps-package-info-btns'>
                                <Button onClick={handleShowContentsModal} variant='secondary'>Shipment contents</Button>
                                <Button onClick={() => setShowSpecialServices(!showSpecialServices)} variant='secondary'>
                                    {showSpecialServices ? 'Hide' : 'Show'} Special services
                                </Button>
                                {selectedPackageIds.length > 0 ?
                                    <Button onClick={updatePackage} disabled={selectedPackageIds.length > 1} variant='secondary'>Update</Button>
                                    :
                                    <Button onClick={addPackage} variant='secondary'>Add</Button>
                                }
                            </div>
                        </div>
                        <div>
                            {/* <fieldset>
                                <legend>Package rate quotes</legend>
                                <div className='mps-details'>
                                    <p>Package rate quote: $0.00</p>
                                </div>
                            </fieldset> */}
                            <fieldset>
                                <legend>Package summary (Total packages: {mpsPackages.length})</legend>
                                <p>If you ship now, your shipment will contain {mpsPackages.length} packages</p>
                                <Table>
                                    <thead>
                                        <tr>
                                            <th>Select</th>
                                            <th>Pkg</th>
                                            <th>Weight</th>
                                            <th>Declared value</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {
                                            mpsPackages.map((p, index) => (
                                                <tr key={p.id}>
                                                    <td>
                                                        <Form.Check
                                                            type='checkbox'
                                                            onChange={handlePackageCheckChange}
                                                            value={p.id}
                                                            checked={selectedPackageIds.includes(p.id)}
                                                        />
                                                    </td>
                                                    <td>{index + 1}</td>
                                                    <td>{p.weight}</td>
                                                    <td>{p.declaredValue}</td>
                                                </tr>
                                            ))
                                        }
                                    </tbody>
                                </Table>
                                <Button onClick={removePackages} variant='secondary' disabled={!selectedPackageIds.length > 0}>Delete</Button>
                            </fieldset>
                        </div>
                    </div>
                </fieldset>
            </Modal.Body>
            <Modal.Footer>
                <Button onClick={closeWithoutSaving} variant='secondary'>Cancel</Button>
                {!onReturnTab && <Button onClick={() => getRateQuoteAndTransitTimes(true)} variant='secondary' disabled={rateQuoteLoading}>
                    {rateQuoteLoading ? <LoadingSpinner /> : 'Rate quote'}
                </Button>}
                <Button onClick={handleClose} variant='secondary'>Save and exit</Button>
                <Button onClick={formik.handleSubmit} className='purple-btn' disabled={submitLoading}>
                    {submitLoading ? <LoadingSpinner /> : 'Ship'}
                </Button>
            </Modal.Footer>
            <ShipmentContentsModal show={showContentsModal} handleClose={handleCloseContentsModal} shipmentContents={shipmentContents} setShipmentContents={updateShipmentContents} revertShipmentContents={revertShipmentContents} />
        </Modal>
    )
}